
import { Box, Paper, Typography, useTheme, LinearProgress } from "@mui/material"
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded"
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded"

const STATS = [
  { label: "Total Revenue", value: "$248,910", delta: "+18.2%", icon: PaymentsRoundedIcon, color: "#16b8a6" },
  { label: "Products", value: "1,284", delta: "+42", icon: Inventory2RoundedIcon, color: "#4f6bed" },
  { label: "In Production", value: "326", delta: "+12.4%", icon: PrecisionManufacturingRoundedIcon, color: "#f59e0b" },
  { label: "Fulfilled", value: "958", delta: "+7.1%", icon: TrendingUpRoundedIcon, color: "#8b5cf6" },
]

const BARS = [42, 58, 49, 72, 63, 88, 76, 95, 81, 68, 92, 100]

const ACTIVITY = [
  { who: "Manufacturing", what: "Batch #4821 completed", time: "2m" },
  { who: "Inventory", what: "Added 120 units · SKU-9032", time: "14m" },
  { who: "Revenue", what: "Logged $4,250 · Wholesale", time: "1h" },
]

export function DashboardMockup() {
  const theme = useTheme()
  const isDark = theme.palette.mode === "dark"

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: isDark
          ? "0 30px 80px -20px rgba(0,0,0,0.6)"
          : "0 30px 80px -28px rgba(14,19,32,0.28)",
      }}
    >
      {/* window bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(14,19,32,0.02)",
        }}
      >
        {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
          <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: c }} />
        ))}
       
      </Box>

      <Box sx={{ display: "flex" }}>
        {/* sidebar */}
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            width: 150,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            p: 1.5,
          }}
        >
          {["Dashboard", "Managers", "Products", "Manufacturing", "Delivery", "Revenue Logs"].map((item, i) => (
            <Box
              key={item}
              sx={{
                px: 1.25,
                py: 0.9,
                borderRadius: 1.5,
                mb: 0.5,
                fontSize: 12.5,
                fontWeight: i === 0 ? 700 : 500,
                color: i === 0 ? "primary.main" : "text.secondary",
                bgcolor: i === 0 ? (isDark ? "rgba(79,107,237,0.14)" : "rgba(79,107,237,0.09)") : "transparent",
              }}
            >
              {item}
            </Box>
          ))}
        </Box>

        {/* main */}
        <Box sx={{ flex: 1, p: { xs: 1.75, sm: 2.5 }, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.75 }}>
            Business Overview
          </Typography>

          {/* stat cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.25, mb: 2 }}>
            {STATS.map((s) => {
              const Icon = s.icon
              return (
                <Box
                  key={s.label}
                  sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fff" }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: 1.5, display: "grid", placeItems: "center", bgcolor: `${s.color}1f` }}>
                      <Icon sx={{ fontSize: 15, color: s.color }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: "#16b8a6", fontWeight: 700 }}>{s.delta}</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 15, letterSpacing: "-0.02em" }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>{s.label}</Typography>
                </Box>
              )
            })}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.5fr 1fr" }, gap: 1.5 }}>
            {/* chart */}
            <Box sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Revenue Trend</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>Last 12 weeks</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75, height: 96 }}>
                {BARS.map((h, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      height: `${h}%`,
                      borderRadius: "4px 4px 0 0",
                      background: "linear-gradient(180deg, #4f6bed 0%, #16b8a6 100%)",
                      opacity: 0.45 + (h / 100) * 0.55,
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* activity */}
            <Box sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 1.25 }}>Recent Activity</Typography>
              {ACTIVITY.map((a, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, mb: 1.25 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main", mt: 0.6, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.3 }}>{a.what}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{a.who} · {a.time} ago</Typography>
                  </Box>
                </Box>
              ))}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>Fulfillment rate</Typography>
                <LinearProgress variant="determinate" value={82} sx={{ height: 6, borderRadius: 3 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}
